import React, { FC, useContext } from 'react';
import styled from 'styled-components';
import cloneDeep from 'lodash/cloneDeep';
import get from 'lodash/get';
import omit from 'lodash/omit';
import set from 'lodash/set';
import Dropdown from 'src/presentation/components/forms/Dropdown';
import { ErrorLabel } from '@ad-scripts/presentation/components/common/forms/control_container';

import FormContext from 'src/presentation/contexts/FormContext';

import InputField, { InputType } from 'src/presentation/components/forms/InputField';
import generateId from 'src/lib/generateId';
import { ChatCampaignTriggerType, ChatCampaignTriggerMatchType } from 'src/models/ChatCampaigns/ChatCampaign';
import { EditorSection, InputFieldContainer } from 'src/presentation/components/pages/shared/EditorSectionLayout';

type DropdownOptions = { label: string; value: string }[];

const triggersOptions: DropdownOptions = [
  { label: 'URL', value: ChatCampaignTriggerType.URL },
  { label: 'Time on page', value: ChatCampaignTriggerType.TIME_ON_PAGE },
  { label: 'Estimated wait time', value: ChatCampaignTriggerType.ESTIMATED_WAIT_TIME },
];

const triggersMatchOptions: DropdownOptions = [
  { label: 'contains', value: ChatCampaignTriggerMatchType.CONTAINS },
  { label: 'exact', value: ChatCampaignTriggerMatchType.EXACT },
  { label: 'regex', value: ChatCampaignTriggerMatchType.REGEX },
];

const ChatCampaignTriggers: FC<any> = ({ attr, 'data-aid': dataAid, errors }) => {
  const getNewItemDefaultData = () => ({
    id: generateId(),
    type: ChatCampaignTriggerType.URL,
    content: {
      matchType: ChatCampaignTriggerMatchType.CONTAINS,
      value: '',
    },
  });

  const renderItem = ({ id, index }) => <ChatCampaignTriggerItem key={id} attr={`${attr}[${index}]`} index={index} />;
  const prefixWord = ({ index }) => {
    return index > 0 ? 'and' : 'when';
  };

  return (
    <EditorSection
      addButtonLabel="Add Trigger"
      attr={attr}
      errors={errors}
      data-aid={dataAid}
      getNewItemDefaultData={getNewItemDefaultData}
      title="Triggers"
      renderItem={renderItem}
      prefixWord={prefixWord}
    />
  );
};

const ChatCampaignTriggerItem: FC<any> = ({ attr }) => {
  const { workingState, updateState } = useContext(FormContext);
  const type: any = get(workingState, `${attr}.type`);

  const onSelectType = value => {
    let newValue = set(cloneDeep(workingState), `${attr}.type`, value);
    if (value === ChatCampaignTriggerType.URL) {
      set(newValue, `${attr}.content.matchType`, ChatCampaignTriggerMatchType.CONTAINS);
    } else {
      newValue = omit(newValue, `${attr}.content.matchType`);
    }

    updateState(newValue, { replaceArrayValues: true });
  };

  switch (type) {
    case ChatCampaignTriggerType.URL: {
      return renderURLTriggerItem(attr, onSelectType);
    }
    case ChatCampaignTriggerType.ESTIMATED_WAIT_TIME: {
      return renderEstimatedWaitTriggerItem(attr, onSelectType);
    }
    case ChatCampaignTriggerType.TIME_ON_PAGE: {
      return renderTimeOnPageTriggerItem(attr, onSelectType);
    }
    default: {
      return null;
    }
  }
};

const renderUrlDropdown = attr => {
  return (
    <DropdownMatchType
      attr={`${attr}.content.matchType`}
      data-aid={`${attr}-content-matchType`}
      fitOpenerWidth={false}
      options={triggersMatchOptions}
    />
  );
};

const renderURLTriggerItem = (attr, onSelectType) => {
  return (
    <UrlContainer>
      <StyledWrappedRow>
        <span>
          <StyledDropdown
            attr={`${attr}.type`}
            data-aid={`${attr}-type`}
            onSelect={onSelectType}
            options={triggersOptions}
            value={null}
          />
        </span>
        <span>{renderUrlDropdown(attr)}</span>
      </StyledWrappedRow>
      <StyledWrappedRow>
        <InputField attr={`${attr}.content.value`} placeholder="example.com" data-aid={`${attr}-content-value`} />
      </StyledWrappedRow>
    </UrlContainer>
  );
};

const renderEstimatedWaitTriggerItem = (attr, onSelectType) => {
  return (
    <StyledWrappedRow>
      <span>
        <StyledDropdown
          attr={`${attr}.type`}
          data-aid={`${attr}-type`}
          onSelect={onSelectType}
          options={triggersOptions}
          value={null}
        />
      </span>
      <IsLabelContainer>is less than</IsLabelContainer>
      <SecondsInput attr={`${attr}.content.threshold`} data-aid={`${attr}-content-threshold`} />
    </StyledWrappedRow>
  );
};

const renderTimeOnPageTriggerItem = (attr, onSelectType) => {
  return (
    <StyledWrappedRow>
      <span>
        <StyledDropdown
          attr={`${attr}.type`}
          data-aid={`${attr}-type`}
          onSelect={onSelectType}
          options={triggersOptions}
          value={null}
        />
      </span>
      <IsLabelContainer>is greater than</IsLabelContainer>
      <SecondsInput attr={`${attr}.content.limit`} data-aid={`${attr}-content-limit`} />
    </StyledWrappedRow>
  );
};

const SecondsInput = ({ attr, 'data-aid': dataAid }) => (
  <>
    <InputFieldContainerWithNoError>
      <InputField type={InputType.number} placeholder="1" attr={attr} data-aid={dataAid} />
    </InputFieldContainerWithNoError>
    <UnitContainer>seconds</UnitContainer>
  </>
);

export const InputFieldContainerWithNoError = styled(InputFieldContainer)`
  & ${ErrorLabel} {
    display: none;
  }
`;

const IsLabelContainer = styled.div`
  margin-left: 8px;
  flex: none;
`;

const StyledWrappedRow = styled.div`
  padding-top: 4px;
  :first-child {
    padding-top: 0px;
  }
  display: flex;
  margin-right: 0px;
  align-items: center;
`;

const DropdownMatchType = styled(Dropdown)`
  margin-left: 8px;
  flex: none;
`;

const StyledDropdown = styled(Dropdown)`
  max-width: 176px;
  min-width: 136px;
  flex: none;
`;

const UnitContainer = styled.div`
  margin-left: 8px;
`;

const UrlContainer = styled.div`
  & ${ErrorLabel} {
    display: none;
  }
`;

export default ChatCampaignTriggers;
